import { computed } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import {
  ContactInfosDto,
  EmailDto,
  MobileNumberDto,
  UserRegistrationDto,
  UserRegistrationIdentityDto,
  UserRegistrationStep,
} from '@workspace/data-types';
import { withStoreDevtools } from '../with-devtools';

export interface UserRegistrationState {
  current: UserRegistrationDto | null;
  loading: boolean;
  error?: string;
}

const initialState: UserRegistrationState = {
  current: null,
  loading: false,
  error: undefined,
};

export const UserRegistrationStore = signalStore(
  { providedIn: 'root' },
  withStoreDevtools('userRegistration'),
  withState(initialState),

  withMethods((store) => {
    return {
      removeCurrentUserRegistration() {
        patchState(store, { current: null });
      },

      confirmEmail() {
        const userRegistration: UserRegistrationDto = store.current() as UserRegistrationDto;
        if (!store.current()) return;
        patchState(store, {
          current: {
            ...userRegistration,
            confirmation: { ...userRegistration.confirmation, email: true },
            step: UserRegistrationStep.PENDING_MOBILE,
          },
        });
      },

      confirmMobileNumber() {
        const userRegistration: UserRegistrationDto = store.current() as UserRegistrationDto;
        if (!store.current()) return;
        patchState(store, {
          current: {
            ...userRegistration,
            confirmation: {
              ...userRegistration.confirmation,
              mobileNumber: true,
            },
            step: UserRegistrationStep.PENDING_PROFILE,
          },
        });
      },

      updateCurrentUserRegistration(userRegistration: UserRegistrationDto) {
        patchState(store, { current: userRegistration });
      },

      updateCurrentUserIdentity(userRegistrationIdentity: UserRegistrationIdentityDto) {
        if (!store.current()) return;
        patchState(store, {
          current: {
            ...(store.current() as UserRegistrationDto),
            identity: {
              ...store.current()?.identity,
              ...userRegistrationIdentity,
            } as UserRegistrationIdentityDto,
            step: UserRegistrationStep.PENDING_USER_CONVERTION,
          },
        });
      },

      updateCurrentUserContactPhoneNumber(mobileNumber: MobileNumberDto) {
        const user: UserRegistrationDto = store.current() as UserRegistrationDto;
        if (!store.current()) return;

        patchState(store, {
          current: {
            ...user,
            contactInfos: { ...user.contactInfos, ...mobileNumber },
            step: UserRegistrationStep.PENDING_MOBILE_VERIFICATION,
          },
        });
      },

      updateCurrentUserContactEmail(email: EmailDto) {
        const user: UserRegistrationDto = store.current() as UserRegistrationDto;
        if (!store.current()) return;

        patchState(store, {
          current: {
            ...user,
            contactInfos: { ...user.contactInfos, ...email },
          },
        });
      },

      updateCurrentUserContactInfos(contactInfos: ContactInfosDto) {
        if (!store.current()) return;
        patchState(store, {
          current: {
            ...(store.current() as UserRegistrationDto),
            contactInfos,
          },
        });
      },
    };
  }),

  withComputed((store) => ({
    currentUserRegistration: computed(() => store.current()),
    isLoading: computed(() => store.loading()),
    hasError: computed(() => !!store.error?.()),
  })),
);
